"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";
import { isIosDevice, isStandaloneDisplay } from "@/lib/push-notifications";
import { ensureInstallPromptListener } from "@/lib/install-prompt";

declare global {
  interface WindowEventMap {
    /** Chromium-only: fired after the browser installs the PWA. Not in
     *  lib.dom (never standardized), so merged here for typed listeners. */
    appinstalled: Event;
  }
}

const LAUNCHED_EVENT = "PWA Launched";
const INSTALL_COMPLETED_EVENT = "Install Completed";

function platformLabel(): "ios" | "android" | "desktop" {
  if (isIosDevice()) return "ios";
  if (/Android/.test(navigator.userAgent)) return "android";
  return "desktop";
}

// Once per page load, not per mount — a remount of the layout island must not
// double-count a launch.
let launchTracked = false;

/**
 * Install-outcome analytics for the PWA. Two signals, both invisible in the
 * funnel events the install card fires (Install Shown/Clicked/Dismissed):
 *
 *  - "PWA Launched": fired once per full page load when running standalone
 *    (installed app window). iOS offers no install-completion event at all,
 *    so launches are the only measurable install outcome there — and being
 *    per-launch, it doubles as an installed-app usage signal everywhere.
 *  - "Install Completed": Chromium's `appinstalled` event. We ship no custom
 *    install button on Chromium (the browser's own promotion is the only
 *    surface), so this is what attributes those installs.
 */
export function PwaAnalytics() {
  useEffect(() => {
    // Capture the Android/Chromium install prompt app-wide and as early as
    // possible (it can fire before the chat surface mounts). Idempotent.
    ensureInstallPromptListener();

    if (!launchTracked && isStandaloneDisplay()) {
      launchTracked = true;
      trackEvent(
        LAUNCHED_EVENT,
        { platform: platformLabel() },
        { interactive: false },
      );
    }

    const onAppInstalled = () => {
      trackEvent(INSTALL_COMPLETED_EVENT, { platform: platformLabel() });
    };
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  return null;
}
